// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { devicesApi } from "../../../Api/devices.api";
import { DeviceTypeId } from "../../../constants/deviceTypes";
import { useDevice } from "../../../Api/newSockets.api";
import dataApi from "../../../Api/data.api";
import mesApi from "../../../Api/mes.api";
import { RECIPE_PARAMETERS, emptyRecipeParameterText, missingRecipeParameters } from "../../../constants/recipeParameters";
import "../../../styles/mes.scss";

// PLC-specific metric display configuration
const PLC_METRICS_CONFIG: Record<string, { label: string; unit: string; color: string; decimals?: number }> = {
  dough_temperature: { label: 'Dough Temp', unit: '°C', color: '#ff9800', decimals: 1 },
  water_temperature: { label: 'Water Temp', unit: '°C', color: '#2196f3', decimals: 1 },
  water_flow: { label: 'Water Flow', unit: 'L/min', color: '#00bcd4', decimals: 2 },
  gross_power: { label: 'Gross Power', unit: 'W', color: '#f44336', decimals: 0 },
  net_power: { label: 'Net Power', unit: 'W', color: '#e91e63', decimals: 0 },
  gross_energy: { label: 'Gross Energy', unit: 'Wh', color: '#9c27b0', decimals: 1 },
  net_energy: { label: 'Net Energy', unit: 'Wh', color: '#673ab7', decimals: 1 },
  homogenisation_power: { label: 'Homog. Power', unit: 'W', color: '#3f51b5', decimals: 0 },
  development_power: { label: 'Dev. Power', unit: 'W', color: '#2196f3', decimals: 0 },
  total_batch_time: { label: 'Batch Time', unit: 's', color: '#009688', decimals: 0 },
  homogenisation_time: { label: 'Homog. Time', unit: 's', color: '#4caf50', decimals: 0 },
  development_time: { label: 'Dev. Time', unit: 's', color: '#8bc34a', decimals: 0 },
  mixer_seq_status: { label: 'Mixer Status', unit: '', color: '#ff5722', decimals: 0 },
  mixer_seq_operation: { label: 'Mixer Op', unit: '', color: '#795548', decimals: 0 },
  // Fermentor metrics (plcFermentor.py / DB901)
  starter_co2: { label: 'Starter CO₂', unit: '%', color: '#607d8b', decimals: 2 },
  fermentor_temperature: { label: 'Fermentor Temp', unit: '°C', color: '#ff9800', decimals: 1 },
  starter_temperature: { label: 'Starter Temp', unit: '°C', color: '#ffc107', decimals: 1 },
  starter_ph: { label: 'Starter pH', unit: 'pH', color: '#8bc34a', decimals: 2 },
  starter_weight: { label: 'Starter Weight', unit: 'kg', color: '#795548', decimals: 1 },
  fermentor_seq_status: { label: 'Fermentor Status', unit: '', color: '#ff5722', decimals: 0 },
  fermentor_seq_operation: { label: 'Fermentor Op', unit: '', color: '#9e9e9e', decimals: 0 },
  fermentor_total_op_time: { label: 'Total Op Time', unit: 's', color: '#009688', decimals: 0 },
  fermentor_running_op_time: { label: 'Running Op Time', unit: 's', color: '#4caf50', decimals: 0 },
};

// Chart groups by unit type
const CHART_GROUPS: Record<string, { label: string; unit: string; color: string; metrics: string[] }> = {
  temperature: {
    label: 'Temperature',
    unit: '°C',
    color: '#ff9800',
    metrics: ['dough_temperature', 'water_temperature', 'fermentor_temperature', 'starter_temperature'],
  },
  fermentation: {
    label: 'Fermentation',
    unit: '',
    color: '#8bc34a',
    metrics: ['starter_co2', 'starter_ph', 'starter_weight'],
  },
  power: {
    label: 'Power',
    unit: 'W',
    color: '#f44336',
    metrics: ['gross_power', 'net_power', 'homogenisation_power', 'development_power'],
  },
  energy: {
    label: 'Energy',
    unit: 'Wh',
    color: '#9c27b0',
    metrics: ['gross_energy', 'net_energy'],
  },
  time: {
    label: 'Time',
    unit: 's',
    color: '#009688',
    metrics: [
      'total_batch_time',
      'homogenisation_time',
      'development_time',
      'fermentor_total_op_time',
      'fermentor_running_op_time',
    ],
  },
};

const DEFAULT_RECIPE_NAME = 'Country 10kg';

interface PlcUsbDeviceMetricsProps {
  device: any;
  deviceId: number;
  metrics: any;
  isOnline: boolean;
}


const PlcUsbDeviceMetrics: React.FC<PlcUsbDeviceMetricsProps> = ({
  device,
  deviceId,
  metrics,
  isOnline,
}) => {
  const isPlcDevice = device?.deviceTypes?.id === DeviceTypeId.PLC;

  // PLC/USB metrics state
  const [plcTimeFilter, setPlcTimeFilter] = useState<'live' | '1h' | '6h' | '24h'>('1h');
  const [plcMetricsData, setPlcMetricsData] = useState<Record<string, any[]>>({});
  const [loadingPlcMetrics, setLoadingPlcMetrics] = useState(false);
  const [copiedField, setCopiedField] = useState<'ip' | 'token' | null>(null);

  // Settings modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | "">(
    device?.settings?.defaultDataset ?? device?.settings?.defaultDataset ?? ""
  );
  const [newDatasetName, setNewDatasetName] = useState("");
  const [creatingDataset, setCreatingDataset] = useState(false);

  // AI Monitoring state (shown for all PLC devices — see isPlcDevice above)
  const { socket, updateDeviceSettings, sendRecipe } = useDevice();

  // AI monitoring state - updated from real-time metrics
  const [aiMonitoringActive, setAiMonitoringActive] = useState<boolean>(false);
  const [aiMonitoringLoading, setAiMonitoringLoading] = useState<boolean>(false);

  // Recipe sending — only relevant for devices with settings.recipeScript configured
  const isRecipeCapable = !!device?.settings?.recipeScript;
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [recipeSending, setRecipeSending] = useState(false);
  const [recipeName, setRecipeName] = useState(DEFAULT_RECIPE_NAME);
  const [recipeValues, setRecipeValues] = useState<Record<string, string>>(emptyRecipeParameterText);
  const [recipeResultLog, setRecipeResultLog] = useState<string[] | null>(null);
  const [recipeResultSuccess, setRecipeResultSuccess] = useState<boolean | null>(null);

  // The values can either be typed in ('manual') or pulled from a product's recipe
  // in MES ('mes'). Either way they stay editable before sending, so a stored
  // recipe can still be nudged for one batch without being changed in MES.
  const [recipeSource, setRecipeSource] = useState<'manual' | 'mes'>('manual');
  const [mesProducts, setMesProducts] = useState<any[]>([]);
  const [mesRecipes, setMesRecipes] = useState<any[]>([]);
  const [mesProductId, setMesProductId] = useState<number | ''>('');
  const [mesRecipeId, setMesRecipeId] = useState<number | ''>('');
  const [mesLoading, setMesLoading] = useState(false);
  const [mesError, setMesError] = useState<string | null>(null);

  const handleRecipeFieldChange = (key: string, value: string) => {
    setRecipeValues((prev) => ({ ...prev, [key]: value }));
  };

  // Products are only needed once the user asks for the MES source, so they are
  // fetched on the switch rather than with the rest of the device screen.
  const handleSelectRecipeSource = async (source: 'manual' | 'mes') => {
    setRecipeSource(source);
    setMesError(null);
    if (source !== 'mes' || mesProducts.length > 0) return;

    setMesLoading(true);
    try {
      setMesProducts(await mesApi.getProducts({ is_active: true }));
    } catch (err: any) {
      setMesError(err?.message || 'Failed to load products');
    } finally {
      setMesLoading(false);
    }
  };

  const handleSelectMesProduct = async (productId: number | '') => {
    setMesProductId(productId);
    setMesRecipeId('');
    setMesRecipes([]);
    setMesError(null);
    if (!productId) return;

    setMesLoading(true);
    try {
      const recipes = await mesApi.getRecipes({ product_id: Number(productId) });
      setMesRecipes(recipes);
      // Jump straight to the version that is actually in production; the dropdown
      // still lists the archived ones for a deliberate re-run of an older recipe.
      const active = recipes.find((r: any) => r.IsActive) || recipes[0];
      if (active) await handleSelectMesRecipe(active.ID, recipes);
    } catch (err: any) {
      setMesError(err?.message || 'Failed to load recipes');
    } finally {
      setMesLoading(false);
    }
  };

  const handleSelectMesRecipe = async (recipeId: number | '', knownRecipes?: any[]) => {
    setMesRecipeId(recipeId);
    setMesError(null);
    if (!recipeId) return;

    setMesLoading(true);
    try {
      const parameters = await mesApi.getRecipeParameters(Number(recipeId));
      const next = emptyRecipeParameterText();
      parameters.forEach((param: any) => {
        if (param.ParamKey in next && param.ParamValue !== null && param.ParamValue !== undefined) {
          next[param.ParamKey] = String(Number(param.ParamValue));
        }
      });
      setRecipeValues(next);

      const missing = missingRecipeParameters(next);
      if (missing.length > 0) {
        setMesError(`Recipe is missing ${missing.length} parameter(s): ${missing.map((m) => m.label).join(', ')}`);
      }

      // Name the recipe off the row itself rather than the product dropdown: when
      // this runs from handleSelectMesProduct the product id was only just set and
      // would still read as the previous selection.
      const recipe = (knownRecipes || mesRecipes).find((r: any) => r.ID === Number(recipeId));
      if (recipe) {
        const productName = recipe.Product_ProductToRecipe?.ProductName || recipe.RecipeCode || 'Recipe';
        // The PLC logs the name, so send something identifiable rather than "Recipe 42".
        setRecipeName(`${productName} v${recipe.RecipeVersion || 1}`);
      }
    } catch (err: any) {
      setMesError(err?.message || 'Failed to load recipe parameters');
    } finally {
      setMesLoading(false);
    }
  };

  const handleSendRecipe = async () => {
    if (recipeSending) return;

    // There are no defaults to fall back on any more, and a blank field must not
    // become a 0 on the way to the PLC — refuse the write instead.
    const missing = missingRecipeParameters(recipeValues);
    if (missing.length > 0) {
      setRecipeResultSuccess(false);
      setRecipeResultLog([`Fill in all values before sending. Missing: ${missing.map((m) => m.label).join(', ')}`]);
      return;
    }

    setRecipeSending(true);
    setRecipeResultLog(null);
    setRecipeResultSuccess(null);
    try {
      const numericValues: Record<string, number> = {};
      RECIPE_PARAMETERS.forEach((f) => {
        numericValues[f.key] = parseFloat(recipeValues[f.key]);
      });
      const result = await sendRecipe(deviceId, numericValues, recipeName.trim() || DEFAULT_RECIPE_NAME);
      setRecipeResultSuccess(!!result?.success);
      setRecipeResultLog(Array.isArray(result?.log) ? result.log : (result?.error ? [String(result.error)] : []));
    } catch (err: any) {
      setRecipeResultSuccess(false);
      setRecipeResultLog([err?.message || 'Failed to send recipe']);
    } finally {
      setRecipeSending(false);
    }
  };


  // Update AI monitoring state from incoming metrics
  useEffect(() => {
    const arr = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
    const forThis = arr.filter((m: any) => m && Number(m.deviceId) === deviceId);

    // Find a metric with explicit ai_monitoring_active boolean
    for (let i = forThis.length - 1; i >= 0; i--) {
      const val = forThis[i]?.ai_monitoring_active;
      if (val === true) {
        setAiMonitoringActive(true);
        setAiMonitoringLoading(false); // State confirmed, stop loading
        return;
      } else if (val === false) {
        setAiMonitoringActive(false);
        setAiMonitoringLoading(false); // State confirmed, stop loading
        return;
      }
    }
  }, [metrics, deviceId]);
  // Datasets are fetched over REST (request/response) rather than via the socket
  // 'datasets' event, which is race-prone on a hard reload: the emit and the event
  // listener attach in separate effects, so a fast response can arrive before the
  // listener exists and is silently dropped, leaving this dropdown empty. A REST GET
  // on mount has no such race and is retryable.
  const [datasets, setDatasets] = useState<any[]>([]);

  const fetchDatasets = async () => {
    try {
      const res = await dataApi.getDatasets(0, 200, { type: 'Metrics' });
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };

  // Fetch datasets once on mount via REST (no socket-event timing race)
  useEffect(() => {
    fetchDatasets();
  }, []);

  // Dataset options
  const datasetOptions = useMemo(() => {
    return datasets?.map((d: any) => ({ value: d.id, label: d.name })) || [];
  }, [datasets]);

  // Update selected dataset when device settings change
  useEffect(() => {
    const datasetId = device?.settings?.defaultDataset ?? device?.settings?.defaultDataset;
    if (datasetId != null) {
      setSelectedDatasetId(Number(datasetId));
    }
  }, [device]);

  // Handlers
  const handleDatasetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : "";
    setSelectedDatasetId(value);
    if (value) {
      await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: value });
    }
  };

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;
    setCreatingDataset(true);
    try {
      const newDataset = await dataApi.createDataset(newDatasetName.trim());
      if (newDataset?.id) {
        setSelectedDatasetId(newDataset.id);
        await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: newDataset.id });
      }
      setNewDatasetName("");
      await fetchDatasets();
    } catch (err) {
      console.error("Failed to create dataset:", err);
    } finally {
      setCreatingDataset(false);
    }
  };

  const copyToClipboard = (text: string, field: 'ip' | 'token') => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Determine theme color based on device type
  const themeColor = isPlcDevice ? '#ff9800' : '#9c27b0';

  // Process incoming real-time metrics
  useEffect(() => {
    const arr = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
    const forThis = arr.filter((m: any) => m && Number(m.deviceId) === deviceId);

    if (forThis.length === 0) return;

    setPlcMetricsData((prev) => {
      const updated = { ...prev };
      forThis.forEach((met: any) => {
        Object.entries(met).forEach(([key, value]) => {
          if (key !== 'deviceId' && key !== 'organizationId' && typeof value === 'number') {
            if (!updated[key]) {
              updated[key] = [];
            }
            updated[key] = [...updated[key], value];
          }
        });
      });
      return updated;
    });
  }, [metrics, deviceId]);

  // Load historical metrics
  useEffect(() => {
    if (!device) return;

    const loadHistorical = async () => {
      setLoadingPlcMetrics(true);
      try {
        let from: Date;
        const now = new Date();
        if (plcTimeFilter === '1h') from = new Date(now.getTime() - 60 * 60 * 1000);
        else if (plcTimeFilter === '6h') from = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        else if (plcTimeFilter === '24h') from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        else from = new Date(now.getTime() - 60 * 60 * 1000); // Default 1h for live

        const historicalData = await devicesApi.getDeviceMetrics(deviceId, { from });

        if (historicalData && historicalData.length > 0) {
          const metricData: Record<string, any[]> = {};
          historicalData.forEach((record: any) => {
            const metricsObj = record.metrics || {};
            Object.entries(metricsObj).forEach(([key, value]) => {
              if (typeof value === 'number') {
                if (!metricData[key]) {
                  metricData[key] = [];
                }
                metricData[key].push(value);
              }
            });
          });
          setPlcMetricsData(metricData);
        }
      } catch (err) {
        console.error('Failed to load historical metrics:', err);
      } finally {
        setLoadingPlcMetrics(false);
      }
    };

    loadHistorical();
  }, [device, deviceId, plcTimeFilter]);

  // PLC Chart groups
  const plcChartGroups = useMemo(() => {
    const groups: { key: string; label: string; unit: string; color: string; data: any }[] = [];

    Object.entries(CHART_GROUPS).forEach(([groupKey, groupConfig]) => {
      const datasets: any[] = [];

      groupConfig.metrics.forEach(metricKey => {
        const data = plcMetricsData[metricKey];
        if (!data || !Array.isArray(data)) return;

        const config = PLC_METRICS_CONFIG[metricKey];
        if (!config) return;

        // Get last 60 points for better history view
        const points = data.slice(-60);
        if (points.length === 0) return;

        datasets.push({
          label: config.label,
          data: points,
          borderColor: config.color,
          backgroundColor: `${config.color}20`,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 3,
        });
      });

      if (datasets.length > 0 && datasets.some(d => d.data.length > 0)) {
        groups.push({
          key: groupKey,
          label: groupConfig.label,
          unit: groupConfig.unit,
          color: groupConfig.color,
          data: {
            labels: Array(60).fill(''),
            datasets,
          },
        });
      }
    });

    return groups;
  }, [plcMetricsData]);

  // Create chart options for PLC metrics
  const createPlcChartOptions = (unit: string, label: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 8,
          font: { size: 11 },
          color: '#c5c5c5',
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(33, 40, 44, 0.95)',
        titleColor: '#c5c5c5',
        bodyColor: '#c5c5c5',
        callbacks: {
          label: (context: any) => {
            const value = context.raw?.toFixed(1) || '0';
            return `${context.dataset.label}: ${value}${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(108, 117, 125, 0.1)',
        },
        ticks: {
          font: { size: 10 },
          color: '#6c757d',
          maxTicksLimit: 5,
        },
        title: {
          display: true,
          text: unit,
          color: '#6c757d',
          font: { size: 10 },
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  });

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Header with time filter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
        borderRadius: '12px',
        border: `1px solid ${themeColor}30`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ fontSize: '1.5rem' }}>
            {isPlcDevice ? '⚙️' : '🔌'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#fff',
            }}>
              {device.name}
            </div>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: isPlcDevice ? 'rgba(255, 152, 0, 0.2)' : 'rgba(233, 30, 99, 0.2)',
              border: `1px solid ${isPlcDevice ? '#ff9800' : '#e91e63'}`,
              color: isPlcDevice ? '#ffb74d' : '#f48fb1',
            }}>
              {device.deviceTypes?.name || (isPlcDevice ? 'PLC' : 'USB')}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Loading indicator */}
          {loadingPlcMetrics && (
            <span style={{ color: themeColor, fontSize: '0.85rem' }}>Loading...</span>
          )}
          {/* AI Monitoring toggle — shown for all PLC devices */}
          {isPlcDevice && (
            <button
              onClick={() => {
                if (aiMonitoringLoading) return; // Prevent double-click while loading
                setAiMonitoringLoading(true);
                const newState = !aiMonitoringActive;
                // Hive routes updateDeviceSettings to the host Nexus socket by deviceId.
                // A PLC is a child device (connectedTo = host device id).
                // We send the host device id so Hive finds the right Nexus socket,
                // and include aiMonitoringDeviceId so Nexus knows which subprocess to target.
                const hostDeviceId = device.connectedTo ?? deviceId;
                socket.emit('updateDeviceSettings', {
                  deviceId: hostDeviceId,
                  settings: {aiMonitoring: newState, aiMonitoringDeviceId: deviceId },
                });
              }}
              disabled={aiMonitoringLoading}
              title={aiMonitoringActive ? 'Turn off AI monitoring' : 'Turn on AI monitoring'}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: `1px solid ${aiMonitoringActive ? '#00ff88' : '#6c757d'}60`,
                background: aiMonitoringActive
                  ? 'rgba(0, 255, 136, 0.15)'
                  : 'rgba(108, 117, 125, 0.15)',
                color: aiMonitoringActive ? '#00ff88' : '#6c757d',
                fontSize: '0.85rem',
                cursor: aiMonitoringLoading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                boxShadow: aiMonitoringActive ? '0 0 8px rgba(0,255,136,0.3)' : 'none',
                opacity: aiMonitoringLoading ? 0.7 : 1,
              }}
            >
              {aiMonitoringLoading ? (
                <span style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid currentColor',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
              ) : (
                <span>🤖</span>
              )}
              AI Monitoring: {aiMonitoringLoading ? '...' : (aiMonitoringActive ? 'ON' : 'OFF')}
            </button>
          )}
          {/* Send Recipe — shown only for devices with settings.recipeScript configured */}
          {isRecipeCapable && (
            <button
              onClick={() => {
                setRecipeResultLog(null);
                setRecipeResultSuccess(null);
                setRecipeModalOpen(true);
              }}
              title="Send recipe to PLC"
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: `1px solid #4caf5060`,
                background: 'rgba(76, 175, 80, 0.15)',
                color: '#4caf50',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span>🧪</span> Send Recipe
            </button>
          )}
          <button
            onClick={() => setSettingsModalOpen(true)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: `1px solid ${themeColor}40`,
              background: `${themeColor}25`,
              color: themeColor,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>⚙️</span> Settings
          </button>
          <span style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '0.5rem' }}>Time range:</span>

          <select
            value={plcTimeFilter}
            onChange={(e) => setPlcTimeFilter(e.target.value as any)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: `1px solid ${themeColor}40`,
              background: 'rgba(33, 40, 44, 0.95)',
              color: '#c5c5c5',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            <option value="live">Live</option>
            <option value="1h">Last 1 hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
          </select>
        </div>
      </div>

      {/* Device Info Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        {/* Status */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${isOnline ? '#00ff8830' : '#ff336630'}`,
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            Status
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flex: 1,
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isOnline ? '#00ff88' : '#ff3366',
              boxShadow: isOnline ? '0 0 6px #00ff88' : '0 0 6px #ff3366',
            }} />
            <span style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: isOnline ? '#00ff88' : '#ff3366',
            }}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* IP Address */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${themeColor}20`,
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            IP Address
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            minWidth: 0,
          }}>
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#fff',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {device?.settings?.public_ip || '—'}
            </div>
            {device?.settings?.public_ip && (
              <button
                onClick={() => copyToClipboard(device?.settings?.public_ip, 'ip')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: copiedField === 'ip' ? '#00ff88' : '#6c757d',
                  transition: 'color 0.2s',
                }}
                title="Copy to clipboard"
              >
                {copiedField === 'ip' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Token */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${themeColor}20`,
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            Device Token
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            minWidth: 0,
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#fff',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {device?.token || '—'}
            </div>
            {device?.token && (
              <button
                onClick={() => copyToClipboard(device?.token, 'token')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: copiedField === 'token' ? '#00ff88' : '#6c757d',
                  transition: 'color 0.2s',
                }}
                title="Copy to clipboard"
              >
                {copiedField === 'token' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Metric Cards Grid */}
      {plcChartGroups.length > 0 ? (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}>
            {plcChartGroups.flatMap(group =>
              group.data.datasets.map((ds: any, idx: number) => {
                const latestValue = ds.data[ds.data.length - 1];
                const config = Object.values(PLC_METRICS_CONFIG).find(c => c.color === ds.borderColor);
                const decimals = config?.decimals ?? 1;
                return (
                  <div
                    key={`${group.key}-${idx}`}
                    style={{
                      background: `linear-gradient(135deg, ${ds.borderColor}15 0%, ${ds.borderColor}08 100%)`,
                      border: `1px solid ${ds.borderColor}40`,
                      borderRadius: '10px',
                      padding: '1rem',
                    }}
                  >
                    <div style={{
                      fontSize: '0.7rem',
                      color: ds.borderColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                    }}>
                      {ds.label}
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: 'monospace',
                    }}>
                      {typeof latestValue === 'number' ? latestValue.toFixed(decimals) : '—'}
                      <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>
                        {group.unit}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Charts Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            {plcChartGroups.map(group => (
              <div
                key={group.key}
                style={{
                  background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: `1px solid ${group.color}30`,
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem',
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: group.color,
                    borderRadius: '2px',
                  }} />
                  <div style={{
                    fontSize: '0.9rem',
                    color: group.color,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}>
                    {group.label}
                  </div>
                </div>
                <div style={{ height: '200px' }}>
                  <Line data={group.data} options={createPlcChartOptions(group.unit, group.label)} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          color: '#6c757d',
          fontSize: '1rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px',
          border: `1px solid ${themeColor}20`,
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
            {isPlcDevice ? '⚙️' : '🔌'}
          </div>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No metrics available</div>
          <div style={{ fontSize: '0.85rem' }}>
            Waiting for device to send metrics data...
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {settingsModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setSettingsModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">{isPlcDevice ? '⚙️' : '🔌'}</div>
                <div className="modal-title-text">
                  <h3>{isPlcDevice ? 'PLC' : 'USB'} SETTINGS</h3>
                  <p className="modal-subtitle">Configure {device.name}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSettingsModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Default Dataset</label>
                <select
                  value={selectedDatasetId}
                  onChange={handleDatasetChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: `1px solid ${themeColor}40`,
                    background: 'rgba(33, 40, 44, 0.95)',
                    color: '#c5c5c5',
                    fontSize: '0.9rem',
                  }}
                >
                  <option value="">Select dataset...</option>
                  {datasetOptions.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Create New Dataset</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newDatasetName}
                    onChange={(e) => setNewDatasetName(e.target.value)}
                    placeholder="Enter dataset name..."
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${themeColor}40`,
                      background: 'rgba(33, 40, 44, 0.95)',
                      color: '#c5c5c5',
                      fontSize: '0.9rem',
                    }}
                  />
                  <button
                    onClick={handleCreateDataset}
                    disabled={creatingDataset || !newDatasetName.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      border: `1px solid ${themeColor}`,
                      background: creatingDataset ? `${themeColor}50` : `${themeColor}30`,
                      color: themeColor,
                      fontSize: '0.9rem',
                      cursor: creatingDataset || !newDatasetName.trim() ? 'not-allowed' : 'pointer',
                      opacity: creatingDataset || !newDatasetName.trim() ? 0.5 : 1,
                    }}
                  >
                    {creatingDataset ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setSettingsModalOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Recipe Modal */}
      {recipeModalOpen && (
        <div className="mes-modal-overlay" onClick={() => !recipeSending && setRecipeModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">🧪</div>
                <div className="modal-title-text">
                  <h3>SEND RECIPE</h3>
                  <p className="modal-subtitle">Write recipe values to {device.name}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => !recipeSending && setRecipeModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label>Values from</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {([
                    { id: 'manual', label: 'Enter manually' },
                    { id: 'mes', label: 'Product recipe (MES)' },
                  ] as const).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelectRecipeSource(option.id)}
                      disabled={recipeSending}
                      style={{
                        flex: 1,
                        padding: '0.6rem',
                        borderRadius: '6px',
                        border: `1px solid ${recipeSource === option.id ? '#4caf50' : '#4caf5040'}`,
                        background: recipeSource === option.id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(33, 40, 44, 0.95)',
                        color: recipeSource === option.id ? '#4caf50' : '#9e9e9e',
                        fontSize: '0.85rem',
                        cursor: recipeSending ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {recipeSource === 'mes' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginTop: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Product</label>
                    <select
                      className="select-classic"
                      value={mesProductId}
                      onChange={(e) => handleSelectMesProduct(e.target.value ? Number(e.target.value) : '')}
                      disabled={recipeSending || mesLoading}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Select product --</option>
                      {mesProducts.map((p: any) => (
                        <option key={p.ID} value={p.ID}>{p.ProductName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Recipe version</label>
                    <select
                      className="select-classic"
                      value={mesRecipeId}
                      onChange={(e) => handleSelectMesRecipe(e.target.value ? Number(e.target.value) : '')}
                      disabled={recipeSending || mesLoading || !mesProductId}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Select recipe --</option>
                      {mesRecipes.map((r: any) => (
                        <option key={r.ID} value={r.ID}>
                          v{r.RecipeVersion || 1}{r.IsActive ? ' (active)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {recipeSource === 'mes' && (mesLoading || mesError) && (
                <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: mesError ? '#ff3366' : '#9e9e9e' }}>
                  {mesError || 'Loading...'}
                </p>
              )}

              <div className="form-group" style={{ marginTop: '1.25rem' }}>
                <label>Recipe Name</label>
                <input
                  type="text"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  disabled={recipeSending}
                  placeholder={DEFAULT_RECIPE_NAME}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #4caf5040',
                    background: 'rgba(33, 40, 44, 0.95)',
                    color: '#c5c5c5',
                    fontSize: '0.9rem',
                  }}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.75rem',
                marginTop: '1.25rem',
              }}>
                {RECIPE_PARAMETERS.map((field) => (
                  <div className="form-group" key={field.key} style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>
                      {field.label}{field.unit ? ` [${field.unit}]` : ''}
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={recipeValues[field.key]}
                      onChange={(e) => handleRecipeFieldChange(field.key, e.target.value)}
                      disabled={recipeSending}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        borderRadius: '6px',
                        border: '1px solid #4caf5040',
                        background: 'rgba(33, 40, 44, 0.95)',
                        color: '#c5c5c5',
                        fontSize: '0.85rem',
                      }}
                    />
                  </div>
                ))}
              </div>

              {recipeResultLog && (
                <div style={{
                  marginTop: '1.25rem',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: `1px solid ${recipeResultSuccess ? '#00ff8850' : '#ff336650'}`,
                  background: recipeResultSuccess ? 'rgba(0,255,136,0.08)' : 'rgba(255,51,102,0.08)',
                  maxHeight: '180px',
                  overflowY: 'auto',
                }}>
                  <div style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: recipeResultSuccess ? '#00ff88' : '#ff3366',
                  }}>
                    {recipeResultSuccess ? '✓ Recipe sent successfully' : '✗ Recipe send failed'}
                  </div>
                  {recipeResultLog.map((line, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', color: '#9e9e9e', fontFamily: 'monospace' }}>
                      {line}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setRecipeModalOpen(false)}
                disabled={recipeSending}
              >
                Close
              </button>
              <button
                onClick={handleSendRecipe}
                disabled={recipeSending}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  border: '1px solid #4caf50',
                  background: recipeSending ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
                  color: '#4caf50',
                  fontSize: '0.9rem',
                  cursor: recipeSending ? 'wait' : 'pointer',
                  opacity: recipeSending ? 0.7 : 1,
                }}
              >
                {recipeSending ? 'Sending...' : 'Send Recipe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlcUsbDeviceMetrics;

